import { ApolloQueryResult } from '@apollo/client';
import { Link } from 'react-router-dom';
import Button, { ButtonGroup } from '@atlaskit/button';
import LoadingButton from '@atlaskit/button/loading-button';
import SectionMessage from '@atlaskit/section-message';
import {
  Maybe,
  PaymentMethod,
  PaymentMethodType,
  PaymentOnTerms,
  PaymentMethodTypeV2,
  Ach,
} from '@css-xp/graphtypes/src/generated-types-new';
import React, { ReactElement, useMemo } from 'react';
import {
  CommerceVisaLegalNote,
  CommerceSubscriptionChargeNote,
  LegalNoteBlack,
} from '@atlassian/commerce-ui/legal-notes';
import { SUBSCRIPTION_CHARGE_NOTE } from '@css-xp/ppc-constants';
import { isAchPaymentMethod } from '@css-xp/ppc-utils';
import { Divider } from '../../divider';
import { EntitlementGroupByCloudSiteTable } from '../../entitlement-utils/src/entitlements-group-by-cloud-site-table/entitlement-grouped-by-cloud-site-table';
import { GeneratePaymentLink } from '../../generate-payment-link';
import { withApollo } from '../../graphql-provider';
import { useNotification, useSidebar } from '../../hooks';
import InflightInvoiceBanner from '../../inflight-invoice-banner/inflight-invoice-banner';
import { errorNotificationBuilder, NotificationTypes, successNotificationBuilder } from '../../notifications';
import { PaymentMethodsTableV2 } from '../../payment-methods-table-v2';
import './manage-payment-methods-sidebar.scss';
import { InfoPanel } from '../../info-panel';

interface PaymentMethodRequest {
  transactionAccountId: string;
  invoiceGroupId?: string;
  id: string;
  name?: string;
}
export interface ManagePaymentMethodsSidebarProps {
  transactionAccountId: string;
  invoiceGroupId: string;
  name?: string;
  allPaymentMethods: Maybe<Maybe<PaymentMethod>[]> | undefined;
  updatePaymentMethodLink?: string;
  defaultPaymentMethodType: string;
  defaultPaymentMethodId: string;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  // eslint-disable-next-line no-unused-vars
  submitAction: (request: PaymentMethodRequest) => Promise<PaymentMethod>;
  isTermPaymentMethodAllowed?: boolean;
  showAdditionalPaymentMethods?: boolean;
  successStateProps?: {
    header: string;
    description: string;
  };
  errorStateProps?: {
    header: string;
    description: ReactElement | string;
  };
  heading?: string;
  showDisclaimer?: boolean;
  disclaimerContent?: Array<string>;
  saveButtonLabel?: string;
  inflightBannerMessage?: string;
  linkedEntitlementHeading?: string;
  termPaymentStatus?: Maybe<string> | null;
  createBillingProfilePayload?: Record<string, unknown>;
  isOneColumnLayout?: boolean;
  isPaypalSupportEnabled?: boolean;
  hideTermPaymentWarningMessage?: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
  shouldShowACHPaymentMethod?: boolean;
}

const defaultSuccessStateProps = {
  header: 'Payment method set successfully.',
  description: '',
};
const defaultErrorStateProps = {
  header: 'Error setting payment method.',
  description: '',
};

type DisclaimerProps = {
  content?: Array<string>;
  version?: number;
};

const Disclaimer = ({ content = [], version }: DisclaimerProps) => {
  if (content.length > 0) {
    return (
      <>
        <Divider />
        {content.map((paragraph, index) => (
          <LegalNoteBlack key={index}>{paragraph}</LegalNoteBlack>
        ))}
      </>
    );
  }

  return (
    <>
      {!version && <Divider />}
      {version === 2 && <LegalNoteBlack>{SUBSCRIPTION_CHARGE_NOTE}</LegalNoteBlack>}
      {!version && <CommerceSubscriptionChargeNote />}
      <CommerceVisaLegalNote />
    </>
  );
};

const ManagePaymentMethodsSidebar = ({
  transactionAccountId,
  allPaymentMethods,
  updatePaymentMethodLink,
  defaultPaymentMethodId,
  invoiceGroupId,
  name,
  refetch,
  submitAction,
  isTermPaymentMethodAllowed = true,
  successStateProps = defaultSuccessStateProps,
  errorStateProps = defaultErrorStateProps,
  heading,
  showDisclaimer,
  disclaimerContent = [],
  saveButtonLabel = 'Save',
  inflightBannerMessage = 'All changes will be reflected to the orders included in the inflight invoice',
  linkedEntitlementHeading,
  createBillingProfilePayload,
  isOneColumnLayout = false,
  isPaypalSupportEnabled,
  hideTermPaymentWarningMessage = false,
  onClose,
  onSuccess,
  shouldShowACHPaymentMethod = false,
}: ManagePaymentMethodsSidebarProps) => {
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = React.useState<string>(defaultPaymentMethodId);
  const [showTermPaymentWarning, setShowTermPaymentWarning] = React.useState<boolean>(false);
  const [disablePaymentTermSelection, setDisablePaymentTermSelection] = React.useState<boolean>(false);
  const [isSubmitLoading, setIsSubmitLoading] = React.useState<boolean>(false);
  const { sendSuccessNotification, notificationSent, setNotificationSent, sendErrorNotification } = useNotification();
  const { closeSidebar, openSidebar } = useSidebar();

  // Show only Uniq Payment on terms method available and prefer to the one which is selected as default payment method in invoice group.
  const deferredPaymentMethods =
    allPaymentMethods?.filter((paymentMethod) => paymentMethod?.type === PaymentMethodType.DEFERRED) ?? [];

  const cardPaymentMethods = useMemo(
    () => allPaymentMethods?.filter((paymentMethod) => paymentMethod?.type === PaymentMethodType.CARD) ?? [],
    [allPaymentMethods]
  );

  const payPalPaymentMethods =
    allPaymentMethods?.filter((paymentMethod: any) => paymentMethod?.type === PaymentMethodTypeV2.PAYPAL) ?? [];

  const achPaymentMethods =
    allPaymentMethods?.filter((paymentMethod: any) => isAchPaymentMethod(paymentMethod?.type)) ?? [];
  const selectPaymentMethod = (id: string) => {
    setSelectedPaymentMethodId(id);
  };

  const makeSuccessNotificationOptions = () => {
    return successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('sucess-manage-payment-method')
      .withHeader(successStateProps.header)
      .withMessage(successStateProps.description)
      .withDismissAfter(5000)
      .build();
  };

  const errorNotificationOptions = (errorMessage?: string) => {
    return errorNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('error-manage-payment-method')
      .withHeader(errorStateProps.header)
      .withMessage(errorMessage ?? errorStateProps.description)
      .withDismissAfter(5000)
      .build();
  };

  const notify = (error?: any) => {
    if (error) {
      sendErrorNotification(errorNotificationOptions(error?.message));
      setNotificationSent(true);
    } else if (!notificationSent) {
      sendSuccessNotification(makeSuccessNotificationOptions());
      setNotificationSent(true);
    }
  };

  const onSetPaymentMethodClicked = async () => {
    if (selectedPaymentMethodId !== '') {
      try {
        setIsSubmitLoading(true);
        await submitAction({
          transactionAccountId,
          id: selectedPaymentMethodId,
          invoiceGroupId,
          name,
        });
        notify();
        setIsSubmitLoading(false);
        closeSidebar();
        if (onSuccess) {
          onSuccess();
        }
        refetch();
      } catch (err) {
        setIsSubmitLoading(false);
        notify(err);
      }
    }
  };

  const isTermPaymentAvailable =
    deferredPaymentMethods.length > 0 && isTermPaymentMethodAllowed && !disablePaymentTermSelection;

  const isDefaultMethodIsCard = cardPaymentMethods?.some(
    (paymentMethod) => paymentMethod?.id === defaultPaymentMethodId
  );

  const paymentMethodsTableProps = {
    cardPaymentMethods: cardPaymentMethods as PaymentMethod[],
    defaultPaymentMethodId,
    selectPaymentMethod: selectPaymentMethod,
    selectedPaymentMethodId,
    header: 'Select from existing methods',
    setShowTermPaymentWarning,
    isCreditManagementEnabled: true,
    termPaymentMethods: deferredPaymentMethods as PaymentOnTerms[],
    payPalPaymentMethods,
    isTermPaymentAvailable,
    isDefaultMethodIsCard,
    termPaymentStatus: createBillingProfilePayload?.termPaymentStatus as string,
    version: isOneColumnLayout ? 2 : undefined,
    isPaypalSupportEnabled,
    achPaymentMethods: achPaymentMethods as Ach[],
    shouldShowACHPaymentMethod,
  };

  return (
    <div className="invoice-group-payment-methods-wrapper" data-testid="manage-invoice-group-payment-methods">
      <div className="heading">
        {!isOneColumnLayout && <h2>{heading ? heading : 'Manage payment method'}</h2>}
        <InflightInvoiceBanner transactionAccountId={transactionAccountId} message={inflightBannerMessage} />
        {isOneColumnLayout && (
          <InfoPanel
            type="warning"
            message="Keep in mind, billing changes will apply to other subscriptions linked to this billing profile."
          />
        )}
        <div className={isOneColumnLayout ? '' : 'linked-subscriptions-container'}>
          <div className="linked-subscriptions-container__table">
            <EntitlementGroupByCloudSiteTable
              invoiceGroupId={invoiceGroupId}
              defaultPaymentMethodId={defaultPaymentMethodId}
              isDefaultMethodIsCard={isDefaultMethodIsCard}
              setDisablePaymentTermSelection={setDisablePaymentTermSelection}
              transactionAccountId={transactionAccountId}
              {...(linkedEntitlementHeading && { header: linkedEntitlementHeading })}
              version={isOneColumnLayout ? 2 : undefined}
            />
          </div>
        </div>
      </div>
      {updatePaymentMethodLink && (
        <div className="payment-method-link">
          <GeneratePaymentLink updatePaymentMethodLink={updatePaymentMethodLink} stacked />
        </div>
      )}
      {!isOneColumnLayout && <Divider />}
      {!showTermPaymentWarning && !isDefaultMethodIsCard && !hideTermPaymentWarningMessage && (
        <div className={!isOneColumnLayout ? 'term-payment-info-container' : undefined}>
          <InfoPanel
            type="warning"
            title="Select a payment method"
            message="You won't be able to switch to a payment term later if you select any other method right now."
          />
        </div>
      )}
      {
        <>
          {(showTermPaymentWarning || !hideTermPaymentWarningMessage) && (
            <SectionMessage title="Unable to switch to payment terms" appearance="warning">
              <p>To pay later by invoice or pay on account, you’ll need to create a new billing profile.</p>
              <br />
              {!isOneColumnLayout && (
                <Link
                  to="#"
                  onClick={() => {
                    openSidebar({
                      sidebarType: 'createBillingProfilePane',
                      sidebarOptions: {
                        sidebarWidth: '600px',
                        isOverlay: true,
                      },
                      payload: createBillingProfilePayload,
                    });
                  }}
                >
                  Create new billing profile
                </Link>
              )}
            </SectionMessage>
          )}
          <PaymentMethodsTableV2 {...paymentMethodsTableProps} />
        </>
      }
      {showDisclaimer && <Disclaimer content={disclaimerContent} version={isOneColumnLayout ? 2 : undefined} />}
      <footer className="footer">
        <ButtonGroup>
          <Button
            appearance="default"
            onClick={() => {
              if (onClose) {
                onClose();
              }
              closeSidebar();
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            appearance="primary"
            onClick={onSetPaymentMethodClicked}
            isDisabled={
              selectedPaymentMethodId === defaultPaymentMethodId ||
              cardPaymentMethods.length +
                deferredPaymentMethods.length +
                payPalPaymentMethods.length +
                achPaymentMethods?.length ===
                0 ||
              showTermPaymentWarning
            }
            isLoading={isSubmitLoading}
          >
            {saveButtonLabel}
          </LoadingButton>
        </ButtonGroup>
      </footer>
    </div>
  );
};

export default withApollo(ManagePaymentMethodsSidebar);
